import { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../common'

import { Attribute } from './attribute'
import { Dimension } from './dimension'
import { Id as _Id } from './id'
import { Identifiable as _Identifiable } from './identifiable'
import { Model } from './model'
import type { Term } from './term'
import { Type } from './type'

export interface Snapshot<TId extends Snapshot.Id = Snapshot.Id>
  extends Model<TId> {
  attributes?: readonly Attribute[]
}
export namespace Snapshot {
  export const isSnapshot: TypeChecker<Snapshot> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.intersection(
        Model.getTypeChecker(Id.isId),
        TypeChecker.Factory.object<Difference<Snapshot, Model<Id>>>({
          attributes: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(Attribute.isAttribute),
          ),
        }),
      ),
  ).usingAlias('Snapshot')
  /**
   * Just checks if the object's ID indicates that it is an snapshot. This can be
   * used to distinguish between various model types without checking that every
   * field of the model is correct.
   */
  export const shouldBeSnapshot = TypeChecker.Factory.lazy(() =>
    Model.getLooseTypeChecker(Type.Snapshot),
  )

  type InputBase = Omit<
    Snapshot,
    'id' | 'attributes' | 'terms' | 'dimension'
  > & {
    termIds?: readonly Term.Id[]
  }
  export interface Input extends InputBase {
    partialId?: PartialId
    /**
     * While all {@link Snapshot} share certain {@link Attribute}, those are
     * fairly minimal. The majority of the meaningful {@link Attribute} should
     * be passed here
     */
    attributes: readonly Attribute.Input.InContext[]
    /**
     * Associated {@link Dimension}, if there is one
     *
     * When given, each record of this {@link Snapshot} will be associated with
     * this {@link Dimension}
     *
     * When omitted, each record of this {@link Snapshot} will be tied to the
     * institution as a whole
     */
    dimension?: Dimension.Identifiable
  }
  export namespace Input {
    export const isInput: TypeChecker<Input> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.intersection(
        TypeChecker.Factory.omit(isSnapshot, ['id', 'attributes']),
        TypeChecker.Factory.object<Difference<Input, InputBase>>({
          attributes: TypeChecker.Factory.list(
            Attribute.Input.InContext.isInContext,
          ),
          dimension: Dimension.Identifiable.isIdentifiable,
          partialId: TypeChecker.Factory.optional(PartialId.isPartialId),
        }),
      ),
    ).usingAlias('Snapshot.Input')

    export type InContext = Omit<Snapshot.Input, 'dimension'>
    export namespace InContext {
      export const isInContext: TypeChecker<InContext> =
        TypeChecker.Factory.omit(isInput, ['dimension'])
    }
  }

  type UpdatesBase = Partial<InputBase>
  export type Updates = UpdatesBase
  export namespace Updates {
    export const isUpdates: TypeChecker<Updates> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.intersection(
          TypeChecker.Factory.omit(isSnapshot, ['id', 'attributes']),
          // NOTE: if we ever need to support more updates, add properties for
          // type checking below
          TypeChecker.Factory.object<Difference<Updates, UpdatesBase>>({}),
        ),
    ).usingAlias('Snapshot.Updates')
  }

  export type Identifiable<
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > =
    | _Identifiable.ForSpecificType<Type.Snapshot, TPartialId, TContext>
    // We can get snapshot ID from attributes on snapshots
    | Attribute.Identifiable<
        PartialId,
        _Id.UsingSeparator.On<
          Id<TPartialId, TContext>,
          _Id.Separator.WithinSection
        >
      >
  export namespace Identifiable {
    export const isIdentifiable: TypeChecker<Identifiable> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(
          _Identifiable.ForSpecificType.getTypeChecker(
            Type.Snapshot,
            Id.Context.isContext,
          ),
          TypeChecker.Factory.extract(
            Attribute.Identifiable.isIdentifiable,
            _Identifiable.getTypeChecker(
              Type.Snapshot,
              Attribute.Id.Context.Using.getTypeChecker(Id.isId),
            ),
          ),
        ),
      ).usingAlias('dms.models.Snapshot.Identifiable')
  }

  export type Id<
    TPartialId extends PartialId = PartialId,
    TContext extends Id.Context = Id.Context,
  > = _Id<Type.Snapshot, TPartialId, TContext>
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      _Id.getTypeChecker(Type.Snapshot, Context.isContext),
    ).usingAlias('Snapshot.Id') as TypeChecker<Id>
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)

    export type Of<TIdentifiable extends Identifiable> =
      TIdentifiable extends Identifiable<infer TPartialId, infer TContext>
        ? Id<TPartialId, TContext>
        : never

    export function from<TIdentifiable extends Identifiable>(
      source: TIdentifiable,
    ): Of<TIdentifiable>
    export function from(
      source: Identifiable | Attribute.Identifiable | Nullish,
    ): Id | undefined
    export function from(
      source: Identifiable | Attribute.Identifiable | Nullish,
    ): Id | undefined {
      if (!source) {
        return undefined
      }
      if (Attribute.Identifiable.isIdentifiable(source)) {
        // For attributes, we get the snapshot ID from the attribute context
        const context = Attribute.Id.Context.from(source)
        if (Attribute.Id.Context.ForAll.isForAll(context)) {
          return undefined
        }
        const holderId = _Id.Separator.swap(
          context,
          _Id.Separator.WithinSection,
          _Id.Separator.BetweenSections,
        )
        // If the holder is not an snapshot ID, we return `undefined`
        return Snapshot.Id.isId(holderId) ? holderId : undefined
      }
      // If this is not an attribute, we should use the standard
      // Identifiable->ID function
      return _Id.from<Type.Snapshot, PartialId, Context>(
        _Identifiable.ForSpecificType.bind(source, Type.Snapshot),
      )
    }

    /**
     * {@link PartialId} of {@link Dimension} in which this {@link Snapshot} can
     * be used, or {@link _Id.Context.None} if this {@link Snapshot} is for the
     * institution as a whole
     */
    export type Context = PartialId | _Id.Context.None
    export namespace Context {
      export const isContext: TypeChecker<Context> = PartialId.isPartialId

      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function from(
        source: Identifiable | Dimension.Identifiable,
      ): Context {
        if (Identifiable.isIdentifiable(source)) {
          return _Id.Context.from(Id.from(source))
        }
        return _Id.Parts.from(Dimension.Id.from(source)).partialId
      }
    }
  }

  /**
   * These {@link Attribute} should exist on all {@link Snapshot}
   *
   * __NOTE:__ this is a function to avoid runtime issues from circular dependencies
   */
  export function getBuiltInAttributeInputs(): readonly Attribute.Input[] {
    const context = Attribute.Id.Context.ForAll.of(Type.Snapshot)
    return (
      [
        Attribute.Input.FOR_ID,
        {
          canBeOptional: false,
          description:
            'All the data in this snapshot is a representation from this particular date',
          isOptional: false,
          name: 'When',
          partialId: PartialId.Standard.create('when'),
          // TODO: maybe use timestamp?
          type: common.value.types.Intrinsic.Date,
        },
      ] satisfies readonly Attribute.Input.InContext[]
    ).map<Attribute.Input>(input => ({ ...input, context }))
  }
}
